import axios, { AxiosError } from "axios";
import { instanceToPlain, plainToInstance } from "class-transformer";
import { ConsentsDto, } from "healthex-common/consent.dtos";
import { err, voidResult } from "healthex-common/result";
import { authenticatedApiConfig, generateURL } from "./BaseApiService";
import { i18nGetCurrentLanguage } from "./i18next";
import { PatientConsentsByStudyDto, StudyPatientConsentTotalsResult, } from "healthex-common/patient.consent.dtos";
import { CheckPatientsExistsInput, } from "healthex-common/recruitment.dtos";
export class PatientService {
    constructor(authenticationProvider, environment) {
        this.authenticationProvider = authenticationProvider;
        this.environment = environment;
    }
    async requestIdentifiedConsent(studyId, studyOrgId, requestConsentInput) {
        const response = await axios.post(`${this.environment.API_BASE_URL}/organizations/${studyOrgId}/studies/${studyId}/request-identified-consent`, requestConsentInput, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async requestEnrollmentConsent(studyId, studyOrgId, requestConsentInput) {
        const response = await axios.post(`${this.environment.API_BASE_URL}/organizations/${studyOrgId}/studies/${studyId}/request-enrollment-consent`, requestConsentInput, authenticatedApiConfig(this.authenticationProvider));
        if (!response.data) {
            return null;
        }
        return response.data;
    }
    async updateHealthRecords(studyId, studyOrgId, requestConsentInput) {
        await axios.post(generateURL(this.environment.API_BASE_URL, `/organizations/:studyOrgId/studies/:studyId/$update-health-records`, {
            studyOrgId,
            studyId,
        }), requestConsentInput, authenticatedApiConfig(this.authenticationProvider));
    }
    async revokeAllPatientConsents(organizationId, patientId, revokeAllConsentsInput) {
        await axios.post(generateURL(this.environment.API_BASE_URL, `/organizations/:organizationId/patients/:patientId/consents/$revokeAll`, {
            organizationId,
            patientId,
        }), instanceToPlain(revokeAllConsentsInput), authenticatedApiConfig(this.authenticationProvider));
    }
    async getPatientStudyConsent(organizationId, patientId, studyId, proofOfConsentPdf) {
        const queryParams = proofOfConsentPdf ? "?proofOfConsentPdf=true" : "";
        const url = `${this.environment.API_BASE_URL}/organizations/${organizationId}/patients/${patientId}/consents/study/${studyId}${queryParams}`;
        const response = await axios.get(url, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async getFaq() {
        const response = await axios.get(`${this.environment.API_BASE_URL}/patient-faq`);
        return response.data;
    }
    async postSearchForStudyConsents(organizationId, patientId, input, start, end) {
        const response = await axios.post(`${this.environment.API_BASE_URL}/organizations/${organizationId}/patients/${patientId}/consents/study/search`, input, authenticatedApiConfig(this.authenticationProvider, { start, end }));
        return { total: response.data.total, results: plainToInstance(ConsentsDto, response.data.results) };
    }
    async postSearchForStudyPendingConsents(organizationId, patientId, input, start, end) {
        const response = await axios.post(`${this.environment.API_BASE_URL}/organizations/${organizationId}/patients/${patientId}/pending-consents/study/search`, input, authenticatedApiConfig(this.authenticationProvider, { start, end }));
        return { total: response.data.total, results: plainToInstance(PatientConsentsByStudyDto, response.data.results) };
    }
    async postSearchForPatientStudyConsents(organizationId, patientId, input, start, end) {
        const response = await axios.post(`${this.environment.API_BASE_URL}/organizations/${organizationId}/patients/${patientId}/patient-consents/study/search`, input, authenticatedApiConfig(this.authenticationProvider, { start, end }));
        return { total: response.data.total, results: plainToInstance(PatientConsentsByStudyDto, response.data.results) };
    }
    async postSearchForPatientStudyConsentsTotals(organizationId, patientId, input, start, end) {
        const response = await axios.post(`${this.environment.API_BASE_URL}/organizations/${organizationId}/patients/${patientId}/patient-consents/study/search/totals`, input, authenticatedApiConfig(this.authenticationProvider, { start, end }));
        return plainToInstance(StudyPatientConsentTotalsResult, response.data);
    }
    async updateStudyConsent(organizationId, patientId, studyId, studyVersionId, consentType, consentUpdate) {
        consentUpdate.language = i18nGetCurrentLanguage();
        await axios.put(`${this.environment.API_BASE_URL}/organizations/${organizationId}/patients/${patientId}/consents/study/${studyId}/${studyVersionId}/${consentType}`, instanceToPlain(consentUpdate), authenticatedApiConfig(this.authenticationProvider));
    }
    async updateStudyConsentBatch(organizationId, patientId, studyId, studyVersionId, consentUpdateBatch) {
        consentUpdateBatch.update.language = i18nGetCurrentLanguage();
        await axios.patch(`${this.environment.API_BASE_URL}/organizations/${organizationId}/patients/${patientId}/consents/study/${studyId}/${studyVersionId}`, instanceToPlain(consentUpdateBatch), authenticatedApiConfig(this.authenticationProvider));
    }
    async updateIASConsent(organizationId, patientId, consentType, consentUpdate) {
        consentUpdate.language = i18nGetCurrentLanguage();
        await axios.put(generateURL(this.environment.API_BASE_URL, `/organizations/:organizationId/patients/:patientId/consents/ias/:consentType`, {
            organizationId,
            patientId,
            consentType,
        }), instanceToPlain(consentUpdate), authenticatedApiConfig(this.authenticationProvider));
    }
    async getPatientPreferences(patientId) {
        const response = await axios.get(`${this.environment.API_BASE_URL}/patients/${patientId}/preferences`, authenticatedApiConfig(this.authenticationProvider));
        return response.data === "" ? null : response.data;
    }
    async updatePatientPreferences(organizationId, patientId, preferencesUpdate) {
        await axios.put(`${this.environment.API_BASE_URL}/organizations/${organizationId}/patients/${patientId}/preferences`, preferencesUpdate, authenticatedApiConfig(this.authenticationProvider));
    }
    async hasPatientConsentedToStudy(organizationId, patientId, studyId, consentType, includeConsentRecord = false) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/organizations/:organizationId/patients/:patientId/consented/study/:studyId/:consentType`, {
            organizationId,
            patientId,
            studyId,
            consentType,
        }), {
            ...authenticatedApiConfig(this.authenticationProvider),
            params: {
                ...(includeConsentRecord && { includeConsentRecord: true }),
            },
        });
        return response.data;
    }
    async hasPatientConsentedToIAS(organizationId, patientId, consentType, includeConsentRecord = false) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/organizations/:organizationId/patients/:patientId/consented/ias/:consentType`, {
            organizationId,
            patientId,
            consentType,
        }), {
            ...authenticatedApiConfig(this.authenticationProvider),
            params: {
                ...(includeConsentRecord && { includeConsentRecord: true }),
            },
        });
        return response.data;
    }
    async getConsentFlowProgress(orgId, patientId, input) {
        const response = await axios.post(`${this.environment.API_BASE_URL}/organizations/${orgId}/patients/${patientId}/consent-flow-progress`, input, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async updateConsentFlowProgress(orgId, patientId, update) {
        var _a, _b, _c;
        try {
            await axios.put(`${this.environment.API_BASE_URL}/organizations/${orgId}/patients/${patientId}/consent-flow-progress`, update, authenticatedApiConfig(this.authenticationProvider));
            return voidResult();
        }
        catch (e) {
            if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 401 && ((_b = e.response) === null || _b === void 0 ? void 0 : _b.data.error)) {
                return err((_c = e.response) === null || _c === void 0 ? void 0 : _c.data.error);
            }
            throw e;
        }
    }
    async postSendPatientStudyInformation(studyId, studyOrgId, requestConsentInput) {
        const response = await axios.post(`${this.environment.API_BASE_URL}/organizations/${studyOrgId}/studies/${studyId}/patients-study-information`, requestConsentInput, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async addPatients(projectId, input) {
        const response = await axios.post(`${this.environment.API_BASE_URL}/v1/projects/${projectId}/patients`, input, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async checkForDuplicates(projectId, studyOrgId, patients) {
        if (patients.length === 0) {
            return [];
        }
        const input = new CheckPatientsExistsInput({
            patients: patients.map((p) => ({ email: p.email, phone: p.phone })),
        });
        const response = await axios.post(`${this.environment.API_BASE_URL}/organizations/${studyOrgId}/projects/${projectId}/patients/exists`, input, authenticatedApiConfig(this.authenticationProvider));
        const result = response.data;
        return result.results;
    }
    async previewNotification(projectId, organizationId, request) {
        const response = await axios.post(`${this.environment.API_BASE_URL}/organizations/${organizationId}/projects/${projectId}/patients-notification-preview`, request, authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
}
